export interface GridLayoutRect {
  x: number;
  y: number;
  width: number;
  height: number;
}

export interface GridLayoutConfig {
  rows: number;
  columns: number;
  gap: number;
}

export interface GridCellLayout {
  row: number;
  col: number;
  x: number;
  y: number;
  width: number;
  height: number;
}

const computeCellSize = (
  rect: GridLayoutRect,
  config: GridLayoutConfig
): { cellWidth: number; cellHeight: number } | null => {
  const { rows, columns, gap } = config;
  if (rows < 1 || columns < 1) return null;

  const cellWidth = (rect.width - (columns - 1) * gap) / columns;
  const cellHeight = (rect.height - (rows - 1) * gap) / rows;
  if (
    !Number.isFinite(cellWidth) ||
    cellWidth <= 0 ||
    !Number.isFinite(cellHeight) ||
    cellHeight <= 0
  ) {
    return null;
  }

  return { cellWidth, cellHeight };
};

/**
 * Pure grid math for the grid tool: cell size/position for a rows x columns
 * layout inside `rect`. Returns an empty array if the config can't produce
 * cells with positive size.
 */
export const computeGridCells = (
  rect: GridLayoutRect,
  config: GridLayoutConfig
): GridCellLayout[] => {
  const size = computeCellSize(rect, config);
  if (!size) return [];
  const { cellWidth, cellHeight } = size;
  const { rows, columns, gap } = config;

  const cells: GridCellLayout[] = [];
  for (let row = 0; row < rows; row++) {
    for (let col = 0; col < columns; col++) {
      cells.push({
        row,
        col,
        x: rect.x + col * (cellWidth + gap),
        y: rect.y + row * (cellHeight + gap),
        width: cellWidth,
        height: cellHeight
      });
    }
  }

  return cells;
};

export interface GridLinePath {
  vertices: { x: number; y: number }[];
}

/**
 * Pure math for the grid tool's painted lines: one straight line per
 * row/column boundary, INCLUDING the outer edges — an R x C grid produces
 * (R + 1) horizontal + (C + 1) vertical lines, each its own independent
 * path. Every edge (interior divider or outer border side) is therefore its
 * own shape, so any single edge — including one side of the outer
 * perimeter — can be individually selected, hidden or deleted afterward,
 * rather than being welded into one rectangle. When `gap` is 0 an interior
 * line sits exactly on the shared cell edge; otherwise it's centered in the
 * gap between the two neighboring cells. Outer edges always sit exactly on
 * the dragged rect's boundary regardless of gap.
 */
export const computeGridLines = (
  rect: GridLayoutRect,
  config: GridLayoutConfig
): GridLinePath[] => {
  const size = computeCellSize(rect, config);
  if (!size) return [];
  const { cellWidth, cellHeight } = size;
  const { rows, columns, gap } = config;

  const left = rect.x;
  const right = rect.x + rect.width;
  const top = rect.y;
  const bottom = rect.y + rect.height;

  const lines: GridLinePath[] = [];

  for (let row = 0; row <= rows; row++) {
    const y =
      row === 0
        ? top
        : row === rows
          ? bottom
          : top + row * cellHeight + (row - 0.5) * gap;
    lines.push({
      vertices: [
        { x: left, y },
        { x: right, y }
      ]
    });
  }

  for (let col = 0; col <= columns; col++) {
    const x =
      col === 0
        ? left
        : col === columns
          ? right
          : left + col * cellWidth + (col - 0.5) * gap;
    lines.push({
      vertices: [
        { x, y: top },
        { x, y: bottom }
      ]
    });
  }

  return lines;
};
